import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import Seo from "@/components/Seo";
import { Calendar, Clock, ChevronLeft, ArrowRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";

// Minimal markdown renderer (supports H2, H3, paragraphs, ordered/unordered lists, bold).
function renderMarkdown(md) {
  const lines = md.split("\n");
  const blocks = [];
  let buffer = [];
  let inList = null; // 'ul' | 'ol' | null

  const flush = () => {
    if (buffer.length === 0) return;
    blocks.push({ type: "p", content: buffer.join(" ") });
    buffer = [];
  };
  const closeList = () => {
    inList = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      flush(); closeList();
      blocks.push({ type: "h2", content: line.slice(3) });
    } else if (line.startsWith("### ")) {
      flush(); closeList();
      blocks.push({ type: "h3", content: line.slice(4) });
    } else if (/^\d+\.\s/.test(line)) {
      flush();
      if (inList !== "ol") { blocks.push({ type: "ol-start" }); inList = "ol"; }
      blocks.push({ type: "li", content: line.replace(/^\d+\.\s/, "") });
    } else if (line.startsWith("- ")) {
      flush();
      if (inList !== "ul") { blocks.push({ type: "ul-start" }); inList = "ul"; }
      blocks.push({ type: "li", content: line.slice(2) });
    } else if (line === "") {
      flush(); closeList();
    } else {
      buffer.push(line);
    }
  }
  flush(); closeList();

  const inline = (txt) =>
    DOMPurify.sanitize(txt.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"), {
      ALLOWED_TAGS: ["strong", "em", "br"],
    });

  const out = [];
  let listBuf = null;
  let listType = null;
  const flushList = () => {
    if (listBuf) {
      const Tag = listType;
      out.push(
        <Tag
          key={out.length}
          className={listType === "ul" ? "list-disc pl-6 my-4 space-y-1.5 text-slate-700" : "list-decimal pl-6 my-4 space-y-1.5 text-slate-700"}
        >
          {listBuf.map((c, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(c) }} />
          ))}
        </Tag>
      );
      listBuf = null;
      listType = null;
    }
  };

  for (const b of blocks) {
    if (b.type === "ul-start") { flushList(); listBuf = []; listType = "ul"; continue; }
    if (b.type === "ol-start") { flushList(); listBuf = []; listType = "ol"; continue; }
    if (b.type === "li") { listBuf?.push(b.content); continue; }
    flushList();
    if (b.type === "h2") {
      out.push(<h2 key={out.length} className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 mt-10 mb-3">{b.content}</h2>);
    } else if (b.type === "h3") {
      out.push(<h3 key={out.length} className="font-heading font-semibold text-xl text-slate-900 mt-6 mb-2">{b.content}</h3>);
    } else if (b.type === "p") {
      out.push(<p key={out.length} className="text-slate-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: inline(b.content) }} />);
    }
  }
  flushList();
  return out;
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const url = `https://bionet-solutions.com/blog/${post.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: post.cover,
    datePublished: post.date,
    author: { "@type": "Organization", name: "BIONET SOLUTIONS" },
    publisher: { "@type": "Organization", name: "BIONET SOLUTIONS" },
    mainEntityOfPage: url,
    keywords: post.keywords?.join(", "),
  };

  return (
    <>
      <Seo
        title={`${post.title} -BIONET SOLUTIONS`}
        description={post.description}
        keywords={(post.keywords || []).join(", ")}
        image={post.cover}
        ogType="article"
        path={`/blog/${post.slug}`}
        schema={schema}
      />

      <article className="container-x max-w-3xl pt-12 pb-16">
        <Link to="/blog" className="inline-flex items-center text-sm text-slate-500 hover:text-brand-green mb-4">
          <ChevronLeft className="h-4 w-4 mr-1" /> {t("nav.blog")}
        </Link>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">{post.category}</div>
        <h1 className="mt-2 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-900">
          {post.title}
        </h1>
        <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">{post.description}</p>
        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(post.date).toLocaleDateString("fr-FR")}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.read_time}</span>
        </div>
        <div className="mt-8 rounded-3xl overflow-hidden ring-1 ring-slate-200">
          <img src={post.cover} alt={post.title} className="w-full h-auto max-h-[480px] object-cover" />
        </div>
        <div className="mt-10 prose-bionet">{renderMarkdown(post.content)}</div>

        <div className="mt-12 rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-slate-200 p-6 sm:p-8">
          <div className="font-heading font-bold text-xl text-slate-900">{t("blog.cta_title")}</div>
          <p className="mt-1 text-sm text-slate-600">{t("blog.cta_subtitle")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/devis" className="btn-primary">{t("common.cta_quote")} <ArrowRight className="h-4 w-4" /></Link>
            <a href="tel:+33649502801" className="btn-secondary">06 49 50 28 01</a>
          </div>
        </div>
      </article>

      <section className="container-x pb-20">
        <h2 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("blog.related_title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {others.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-2xl bg-white border border-slate-200 ring-soft overflow-hidden hover:-translate-y-0.5 transition">
              <img src={p.cover} alt={p.title} className="w-full aspect-[16/10] object-cover" />
              <div className="p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue font-bold">{p.category}</div>
                <div className="mt-1 font-heading font-bold text-slate-900">{p.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
